/**
 * ew-hero-video.js — reduced-motion gate for the hero banner's background video.
 *
 * The <video> ships with `autoplay muted loop playsinline` in the HTML, so it plays with
 * this file blocked — that is the design, and it is the behaviour with JavaScript off.
 * What this adds is the one thing markup cannot express: `prefers-reduced-motion: reduce`
 * has no CSS hook that stops playback, so a visitor who has asked their operating system
 * for less motion would otherwise get a looping video anyway.
 *
 * When reduce is set the video is paused and rewound to its first frame, which is the same
 * image as the poster, so the box is never empty. The listener is live, so toggling the OS
 * setting takes effect without a reload.
 *
 * No library, no dependency, no external request — `docs/LIQUID-STANDARD.md` §0.
 * Idempotent: every video is stamped once and skipped on later passes.
 */
(() => {
  const QUERY = window.matchMedia('(prefers-reduced-motion: reduce)');

  const apply = (video) => {
    if (QUERY.matches) {
      video.autoplay = false;
      video.pause();
      try {
        video.currentTime = 0;
      } catch (e) {
        /* a video that has not loaded metadata yet cannot be seeked; the poster still shows */
      }
    } else if (video.dataset.ewHeroVideoAutoplay === 'true' && video.paused) {
      const attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
    }
  };

  const init = (video) => {
    if (video.hasAttribute('data-ew-hero-video-ready')) return;
    video.setAttribute('data-ew-hero-video-ready', '');
    apply(video);
  };

  const scan = () => {
    const videos = document.querySelectorAll('[data-ew-hero-video]');
    videos.forEach(init);
    return videos;
  };

  QUERY.addEventListener('change', () => {
    document.querySelectorAll('[data-ew-hero-video]').forEach(apply);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan, { once: true });
  } else {
    scan();
  }

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-ew-hero-video]').forEach(init);
  });
})();
